import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import { StyledInput } from 'components/final-form/Input';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import React, { ChangeEvent, Component, createRef } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Header, Icon, Input as SemanticInput, InputOnChangeData, Message } from 'semantic-ui-react';
import { addTestToCentralPool, getPoolTestKits, removeTestFromCentralPool } from 'services/poolTestkitServices';
import styled from 'styled-components';
import { DetailedPoolTestKitOverviewDto } from 'ts-types/api.types';
import { isKeyCheck } from 'utils/keyUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const PageActionsContainer = styled.div`
  display: flex;
  flex-direction: column;

  .page-actions {
    justify-self: start;
    margin-top: 0.75rem;

    .actions-right {
      float: right;

      .ui.button {
        margin-left: 0.5rem;
      }

      .search-form {
        display: inline-block;
        margin-right: 1rem;

        label {
          margin-right: 1rem;
        }

        .ui.input {
          min-width: 250px;
          margin-right: 1rem;
        }
      }
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 0;
`;

interface Props extends RouteComponentProps<any>, AuthConsumerRenderProps, MessageSourceProps {
}

interface State {
  testKitCode: string,
  poolTestId: number,
  poolTestKitCode: string,
  poolTestKitCompanyDescription?: string,
  testKits: DetailedPoolTestKitOverviewDto[],
  testKitsLoaded: boolean,
  testKitSubmit: boolean,
  errorMessages: Array<string>,
  cancelTokenSource: CancelTokenSource;
}

class PoolTestRegistration extends Component<Props, State> {

  testKitCodeInputRef = createRef<SemanticInput>();

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const state: any = props.location.state!;

    this.state = {
      testKitCode: "",
      poolTestId: state.poolTestId,
      poolTestKitCode: state.poolTestKitCode,
      testKits: [],
      testKitsLoaded: true,
      testKitSubmit: false,
      errorMessages: [],
      cancelTokenSource
    };

    setTimeout(() => {
      this.fetchTestKits();
    }, 5);
  }

  fetchTestKits = async () => {
    const {poolTestId, cancelTokenSource} = this.state;

    this.clearErrors();

    this.setState({
      testKitsLoaded: false
    });

    try {
      const responseDto = await getPoolTestKits(poolTestId, cancelTokenSource);
      this.setState({
        testKits: responseDto.poolTestkitOverview,
        poolTestKitCompanyDescription: responseDto.companyDescription
      });
    } catch (ex) {
      this.handleError(ex);
    } finally {
      this.setState({
        testKitsLoaded: true
      });
    }
  };

  handleError = (exception: any) => {
    const {getMessage} = this.props;

    const error = exception!.response!.data;

    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        "COVID_POOL_TEST_CHANGE_NOT_ALLOWED",
        "COVID_TEST_CHANGE_NOT_ALLOWED",
        "COVID_TEST_ALREADY_IN_ANOTHER_POOL",
        "POOL_COMPANY_TYPE_MISMATCH",
        "POOL_REPETITIVE_TESTING_MISMATCH",
        "POOL_MEMBER_TEST_WITH_NO_PARENT_GUARANTOR",
        "POOL_PARENT_GUARANTOR_COMPANY_MISMATCH",
        "NO_POOLING_FOR_PATIENT_WITH_RECENT_POSITIVE_TEST"
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`poolTestKitEdit.error.${violation.errorCode}`));
          }
        });
      }

      if (!this.state.errorMessages.length) {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });

    } else {
      this.clearErrors();
    }
  };

  clearErrors = () => {
    this.setState({
      errorMessages: []
    });
  };

  setTestKitCode = (evt: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    this.setState({
      testKitCode: data.value
    });
  };

  onTestKitCodeKeyDown = (event: any) => {
    event.persist();
    const isKey: any = isKeyCheck(event);

    if (isKey.enter) {
      this.addTestKitCodeToPool();
    }
  };

  addTestKitCodeToPool = async () => {
    const {testKitCode, poolTestId, cancelTokenSource} = this.state;

    this.clearErrors();

    this.setState({
      testKitSubmit: true
    });

    try {
      await addTestToCentralPool(poolTestId, testKitCode, cancelTokenSource);
      this.setState({
        testKitCode: ""
      });
      this.testKitCodeInputRef.current!.focus();
      this.fetchTestKits();
    } catch (e) {
      this.testKitCodeInputRef.current!.focus();
      this.testKitCodeInputRef.current!.select();
      this.handleError(e);
    } finally {
      this.setState({
        testKitSubmit: false
      });
    }
  };

  removeTestKitCodeFromPool = async (testKitCode: string) => {
    const {poolTestId, cancelTokenSource} = this.state;

    this.clearErrors();

    try {
      await removeTestFromCentralPool(poolTestId, testKitCode, cancelTokenSource);
      this.setState({
        testKitCode: ""
      });
      this.fetchTestKits();
    } catch (e) {
      this.handleError(e);
    }
  };

  render(): React.ReactNode {

    const {getMessage, getMessageWithNamedParams} = this.props;
    const {
      testKitCode,
      errorMessages,
      testKitsLoaded,
      poolTestKitCode,
      poolTestKitCompanyDescription,
      testKitSubmit
    } = this.state;

    const state: any = this.props.location.state;
    const registeredTestkitCode = (state && state.registeredTestkitCode) ? state.registeredTestkitCode : undefined;

    let headerMessageText = "";
    let headerMessageParts: Array<string> = [];

    if (registeredTestkitCode) {
      headerMessageText = getMessageWithNamedParams(
          "pooledTestKitEdit.registrationSuccess", {code: registeredTestkitCode});
      headerMessageParts = headerMessageText.split("###");
    }

    return <PraxStyledSegment style={{height: "100%"}}>

      {
        registeredTestkitCode &&
        <Message success>
          <div className="header-message-text">
            {headerMessageParts[0]}<b>{headerMessageParts[1]}</b>{headerMessageParts[2]}
          </div>
        </Message>
      }

      <StyledHeader as="h3">
        {`${getMessageWithNamedParams("poolTestKitEdit.title", {code: poolTestKitCode})}${poolTestKitCompanyDescription ? " - " + poolTestKitCompanyDescription : ""}`}
      </StyledHeader>

      {errorMessages.length > 0 &&
      <div className="error">
        <StyledErrorMessage onDismiss={() => this.clearErrors()}>
          {errorMessages.map(err => <div key={err}>{err}</div>)}
        </StyledErrorMessage>
      </div>
      }

      <StyledViewTableRow>
        {!testKitsLoaded
            ? <LoaderComponent message={getMessage("poolTestkitEdit.table.loading")}/>
            : this.renderTestKitsTable()
        }
      </StyledViewTableRow>

      <PageActionsContainer>
        <div className="page-actions">
          <div className="actions-right">
            <div className="search-form">
              <label>{getMessage("poolTestKitEdit.addTest.testKitCode")}</label>
              <StyledInput
                  ref={this.testKitCodeInputRef}
                  placeholder=""
                  value={testKitCode}
                  onChange={this.setTestKitCode}
                  onKeyDown={(e: any) => this.onTestKitCodeKeyDown(e)}
              />

              <Button
                  type="button"
                  primary
                  onClick={this.addTestKitCodeToPool}
                  disabled={testKitSubmit}
              >
                {getMessage("poolTestKitEdit.addTest.addAction")}
              </Button>

              <Button
                  type="button"
                  color={"grey"}
                  onClick={(evt) =>
                      this.props.history.push("/lab-central/pool-test-registration")
                  }
              >
                {getMessage("action.back")}
              </Button>
            </div>
          </div>
        </div>
      </PageActionsContainer>

    </PraxStyledSegment>;
  }

  renderTestKitsTable = (): JSX.Element => {

    const {getMessage} = this.props;
    const {testKits} = this.state;

    return (
        <StyledTable
            rowCount={testKits.length}
            rowGetter={this.testKitsRowGetter}
            rowRenderer={this.testKitsRowRenderer}
            columns={[
              {
                width: 200,
                label: getMessage("poolTestKitEdit.tableHeader.employee"),
                dataKey: "testKitId",
                flexGrow: 1,
                cellRenderer: this.employeeCellRenderer
              },
              {
                width: 200,
                label: (getMessage("poolTestKitEdit.tableHeader.testKitNum")),
                flexGrow: 2,
                dataKey: "testKitCode"
              },
              {
                width: 120,
                label: (getMessage("actions")),
                dataKey: "poolTestId",
                cellRenderer: this.actionCellRenderer
              }
            ]}
        />

    );
  };

  testKitsRowGetter = ({index}: any) => {
    const {testKits} = this.state;
    Object.assign(testKits[index], {index: index + 1});

    return testKits[index];
  };

  testKitsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    // const {testKitRegistrations} = this.state;
    // const registration = testKitRegistrations[index];

    let rowStyle = {...style};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={rowStyle}
        >
          {columns}
        </div>
    );
  };

  employeeCellRenderer = (data: any) => {
    const {rowData} = data;
    return <div>
      {`${rowData.companyDescription}: ${rowData.employeeFirstName} ${rowData.employeeLastName}`}
    </div>;
  };

  actionCellRenderer = ({rowData}: any) => {
    const {getMessage, getMessageWithNamedParams} = this.props;

    return <div className="row-actions">
      <DeleteActionButton
          popupMessage={
            getMessageWithNamedParams("testKitRegistration.action.deleteConfirmMessage",
                {
                  code: rowData.testKitCode,
                  employee: `${rowData.employeeFirstName} ${rowData.employeeLastName}`
                })
          }
          hoverMessage={getMessage("testKitRegistration.action.deleteHoverMessage")}
          onConfirm={() => this.removeTestKitCodeFromPool(rowData.testKitCode)}
          trigger={<Icon name={'trash alternate outline'}/>}
      />
    </div>;
  };

}

export default withRouterWorkaround(
    withAuthContext(
        withMessages(
            PoolTestRegistration)));
